import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation, matchPath } from 'react-router'
import { createPortal } from 'react-dom'
import styled from '@emotion/styled'
import {
  FoodLoading,
  Markdown,
  IcebreakerChips,
  FollowUpPromptChips,
  ChartedArtifact,
  OpportunityCard,
  RiskCard,
} from '@instacart/aioli-ui'
import {
  colors,
  spacing,
  borderRadius,
  fontSize,
} from '@retailer-platform/shared-components/src/foundation'
import { Flex, MetricCard, Tooltip } from '@retailer-platform/shared-components/src/tds'
import useAccessControl from '../../legacy/components/AccessControl/useAccessControl'
import { FeatureToggle } from '../../legacy/contexts/featureToggles/FeatureToggleContext'
import { URL_PARTNER_HALT_TERMS } from '../../exports/routing'
import { useDashMessages } from '../../intl/intl.hooks'
import { usePartnerId } from '../../utils/routing/params.hooks'
import { useRetailerAgentChat } from './hooks/useRetailerAgentChat'
import { useRetailerAgentEnabled } from './hooks/useRetailerAgentEnabled'
import { useRetailerAgentSessionContext, useRetailerAgentPanel } from './RetailerAgentPageContext'
import { useConversationHistory } from './hooks/useConversationHistory'
import { ConversationHistoryDropdown } from './ConversationHistoryDropdown'
import { ApprovalCard } from './components/ApprovalCard'
import { ApprovalCardSettled } from './components/ApprovalCardSettled'

const SIDEBAR_WIDTH = 400

const messageIds = {
  title: 'retailerAgent.title' as const,
  placeholder: 'retailerAgent.chat.placeholder' as const,
  placeholderPendingApproval: 'retailerAgent.composer.pendingApproval' as const,
  promptSummarize: 'retailerAgent.prompts.summarize' as const,
  promptTopProduct: 'retailerAgent.prompts.topProduct' as const,
  promptStoreHours: 'retailerAgent.prompts.storeHours' as const,
  promptInsight: 'retailerAgent.prompts.insight' as const,
  helpButton: 'retailerAgent.chat.helpButton' as const,
}

const Sidebar = styled.div<{ isOpen: boolean }>(({ isOpen }) => ({
  position: 'fixed',
  top: 0,
  right: isOpen ? 0 : -SIDEBAR_WIDTH - 32,
  width: SIDEBAR_WIDTH,
  height: '100vh',
  zIndex: 1600,
  background: colors.GRAYSCALE.X0,
  boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
  display: 'flex',
  flexDirection: 'column',
  transition: 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}))

const Header = styled.div({
  display: 'flex',
  alignItems: 'center',
  height: spacing.X56,
  padding: `0 ${spacing.X16}`,
  borderBottom: `1px solid ${colors.GRAYSCALE.X20}`,
  flexShrink: 0,
  gap: spacing.X8,
})

const MessagesPane = styled.div({
  flex: 1,
  overflowY: 'auto',
  padding: `${spacing.X16} ${spacing.X20}`,
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X12,
})

const Bubble = styled.div<{ isUser: boolean }>(({ isUser }) => ({
  maxWidth: '80%',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  padding: `${spacing.X8} ${spacing.X12}`,
  borderRadius: isUser
    ? `${borderRadius.X12} ${borderRadius.X12} ${borderRadius.X4} ${borderRadius.X12}`
    : `${borderRadius.X12} ${borderRadius.X12} ${borderRadius.X12} ${borderRadius.X4}`,
  background: isUser ? colors.PRIMARY_TDS.DARK : colors.GRAYSCALE.X10,
  color: isUser ? colors.GRAYSCALE.X0 : colors.GRAYSCALE.X80,
  fontSize: fontSize.X14,
  lineHeight: 1.5,
  whiteSpace: isUser ? 'pre-wrap' : undefined,
}))

const StatusRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.X8,
  padding: `${spacing.X4} 0`,
  color: colors.GRAYSCALE.X60,
  fontSize: fontSize.X14,
})

const InputWrapper = styled.div({
  padding: `${spacing.X12} ${spacing.X16} ${spacing.X16}`,
  borderTop: `1px solid ${colors.GRAYSCALE.X20}`,
  flexShrink: 0,
})

const ChatTextarea = styled.textarea({
  flex: 1,
  resize: 'none',
  border: `1px solid ${colors.GRAYSCALE.X30}`,
  borderRadius: borderRadius.X8,
  padding: `${spacing.X8} ${spacing.X12}`,
  fontSize: fontSize.X14,
  lineHeight: 1.5,
  fontFamily: 'inherit',
  outline: 'none',
  maxHeight: '120px',
  overflowY: 'auto',
  ':focus': {
    borderColor: colors.PRIMARY_TDS.REGULAR,
  },
})

const SendButton = styled.button<{ isStreaming: boolean }>(({ isStreaming }) => ({
  flexShrink: 0,
  width: spacing.X36,
  height: spacing.X36,
  borderRadius: '50%',
  background: isStreaming ? colors.GRAYSCALE.X30 : colors.PRIMARY_TDS.REGULAR,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.GRAYSCALE.X0,
  fontSize: fontSize.X18,
}))

const HeaderIconButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  background: 'transparent',
  color: colors.GRAYSCALE.X70,
  border: `1px solid ${colors.GRAYSCALE.X30}`,
  borderRadius: '50%',
  cursor: 'pointer',
  fontSize: fontSize.X14,
  lineHeight: 1,
  flexShrink: 0,
  ':hover': {
    background: colors.GRAYSCALE.X10,
  },
})

const HeaderActions = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.X4,
  flexShrink: 0,
})

const AioliBlock = styled.div({
  alignSelf: 'flex-start',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X12,
})

// Raw block from Aioli's save_response. Python-originated payloads use snake_case;
// JS wire format (SDK-normalised) uses camelCase. Both variants appear in practice.
// TODO: replace dual-key handling with a single typed import once the Aioli SDK
// hardens its wire contract to strict protobuf (post-PoC).
type RawBlock = {
  type: string
  // narrative
  markdown_text?: unknown
  markdownText?: unknown
  // metric (field names identical in both formats)
  label?: unknown
  value?: unknown
  // trend
  chart_type?: unknown
  chartType?: unknown
  resolved_artifact_data?: unknown
  resolvedArtifactData?: unknown
  // opportunity
  title?: unknown
  rationale?: unknown
  proposed_action?: unknown
  proposedAction?: unknown
  illustration?: unknown
  priority?: unknown
  impact_estimate?: unknown
  impactEstimate?: unknown
  // risk
  severity?: unknown
  headline?: unknown
  description?: unknown
  sources?: unknown
  affected_entity?: unknown
  affectedEntity?: unknown
  suggested_action?: unknown
  suggestedAction?: unknown
}

// Aioli proto enum values for chart types (mirrored from ResponseCard.tsx in aioli-ui).
const PROTO_CHART_TYPE_MAP: Record<string, 'line' | 'column'> = {
  CHART_TYPE_LINE: 'line',
  CHART_TYPE_BAR: 'column',
}

// Handle both camelCase (JS wire) and snake_case (Python-originated) field names.
function str(a: unknown, b: unknown): string {
  return String(a ?? b ?? '')
}

function renderAioliBlocks(
  blocks: unknown[],
  onFollowUpClick: (text: string) => void
): React.ReactNode[] {
  type MetricItem = { label: string; value: string }
  type InlineGroup =
    | { kind: 'narrative'; text: string; key: string }
    | { kind: 'metrics'; items: MetricItem[]; key: string }
    | {
        kind: 'trend'
        tabularData: Record<string, unknown>[]
        chartType: 'line' | 'column' | undefined
        key: string
      }

  const inline: InlineGroup[] = []
  // Opportunities and risks are collected across the response and rendered together at the end,
  // matching the behavior of ResponseCard in @instacart/aioli-ui.
  const opps: Parameters<typeof OpportunityCard>[0]['blocks'] = []
  const risks: Parameters<typeof RiskCard>[0]['blocks'] = []

  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i] as RawBlock
    if (b.type === 'narrative') {
      inline.push({ kind: 'narrative', text: str(b.markdown_text, b.markdownText), key: `n-${i}` })
    } else if (b.type === 'metric') {
      const last = inline[inline.length - 1]
      const item = { label: String(b.label ?? ''), value: String(b.value ?? '') }
      if (last?.kind === 'metrics') {
        last.items.push(item)
      } else {
        inline.push({ kind: 'metrics', items: [item], key: `m-${i}` })
      }
    } else if (b.type === 'trend') {
      const rawData = b.resolved_artifact_data ?? b.resolvedArtifactData
      const tabularData = Array.isArray(rawData) ? (rawData as Record<string, unknown>[]) : []
      const rawChartType = str(b.chart_type, b.chartType)
      const chartType = PROTO_CHART_TYPE_MAP[rawChartType]
      inline.push({ kind: 'trend', tabularData, chartType, key: `t-${i}` })
    } else if (b.type === 'opportunity') {
      opps.push({
        type: 'opportunity',
        title: str(b.title, b.title),
        rationale: str(b.rationale, b.rationale),
        proposedAction: str(b.proposed_action, b.proposedAction),
        illustration: str(b.illustration, b.illustration),
        priority: Number(b.priority ?? 0),
        impactEstimate: str(b.impact_estimate, b.impactEstimate),
      })
    } else if (b.type === 'risk') {
      risks.push({
        type: 'risk',
        severity: str(b.severity, b.severity),
        headline: str(b.headline, b.headline),
        description: str(b.description, b.description),
        sources: Array.isArray(b.sources) ? (b.sources as (typeof risks)[0]['sources']) : [],
        affectedEntity: str(b.affected_entity, b.affectedEntity),
        suggestedAction: str(b.suggested_action, b.suggestedAction),
      })
    }
  }

  const nodes: React.ReactNode[] = inline.map(g => {
    if (g.kind === 'narrative') return <Markdown key={g.key}>{g.text}</Markdown>
    if (g.kind === 'trend') {
      return (
        <ChartedArtifact
          key={g.key}
          tabularData={g.tabularData}
          chartType={g.chartType}
          flushOuterMargins
        />
      )
    }
    return (
      <MetricCard.Group key={g.key} readOnly>
        {g.items.map((item, idx) => (
          <MetricCard key={idx} title={item.label} value={item.value} />
        ))}
      </MetricCard.Group>
    )
  })

  if (opps.length > 0) {
    nodes.push(<OpportunityCard key="opps" blocks={opps} onFollowUpClick={onFollowUpClick} />)
  }
  if (risks.length > 0) {
    nodes.push(<RiskCard key="risks" blocks={risks} onFollowUpClick={onFollowUpClick} />)
  }

  return nodes
}

function RetailerAgentOverlayInner() {
  const { isPanelOpen: isOpen, setIsPanelOpen: setIsOpen } = useRetailerAgentPanel()
  const [sessionHistoryRefreshTrigger, setSessionHistoryRefreshTrigger] = useState(0)
  const [inputValue, setInputValue] = useState('')
  const partnerId = usePartnerId()
  const chatInputRef = useRef<HTMLTextAreaElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const messages = useDashMessages(messageIds)
  const prevChatMessageCountRef = useRef(0)

  const sessionContext = useRetailerAgentSessionContext()

  const {
    messages: chatMessages,
    isStreaming,
    isToolRunning,
    isRestoringHistory,
    toolName,
    sendMessage,
    confirmApproval,
    cancelApproval,
    stopStreaming,
    startNewChat,
    switchToSession,
    hasPendingApproval,
  } = useRetailerAgentChat(sessionContext)

  const pendingApproval = chatMessages.find(m => m.role === 'approval' && m.status === 'pending')

  const { sessions, isLoading: isLoadingHistory } = useConversationHistory(
    partnerId,
    sessionHistoryRefreshTrigger
  )

  // Increment refresh trigger when a new session is created (first assistant message appears
  // after an empty start — detectable by going from 0 messages to messages with user+assistant)
  useEffect(() => {
    const prev = prevChatMessageCountRef.current
    const curr = chatMessages.length
    prevChatMessageCountRef.current = curr

    if (prev === 0 && curr >= 2) {
      setSessionHistoryRefreshTrigger(t => t + 1)
    }
  }, [chatMessages])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages, isToolRunning, pendingApproval])

  useEffect(() => {
    if (isOpen) {
      chatInputRef.current?.focus()
    }
  }, [isOpen])

  const handleStartNewChat = useCallback(() => {
    startNewChat(partnerId)
    prevChatMessageCountRef.current = 0
    // Bug 4 fix: immediately refresh the session list so the just-exited
    // session appears in the dropdown without requiring a page reload.
    setSessionHistoryRefreshTrigger(t => t + 1)
  }, [startNewChat, partnerId])

  const firstUserMessage = chatMessages.find(m => m.role === 'user')
  // Guard against the approval and aioli branches which have no `content` field.
  const currentTitle =
    firstUserMessage && firstUserMessage.role !== 'approval' && firstUserMessage.role !== 'aioli'
      ? firstUserMessage.content.slice(0, 60)
      : undefined

  const composerDisabled = hasPendingApproval

  return createPortal(
    <>
      <Sidebar isOpen={isOpen}>
        <Header>
          <ConversationHistoryDropdown
            sessions={sessions}
            isLoading={isLoadingHistory}
            currentTitle={currentTitle}
            onSelect={sessionId => {
              void switchToSession(sessionId, partnerId)
            }}
          />
          <HeaderActions>
            <HeaderIconButton
              onClick={handleStartNewChat}
              aria-label="Start new chat"
              title="Start new chat"
            >
              +
            </HeaderIconButton>
            <Tooltip label={messages.helpButton}>
              <HeaderIconButton
                onClick={() =>
                  window.open(
                    'https://instacart.enterprise.slack.com/archives/C0AUZ9WLQC9',
                    '_blank'
                  )
                }
                aria-label={messages.helpButton}
              >
                ?
              </HeaderIconButton>
            </Tooltip>
            <HeaderIconButton onClick={() => setIsOpen(false)} aria-label="Close">
              ✕
            </HeaderIconButton>
          </HeaderActions>
        </Header>

        <MessagesPane>
          {chatMessages.length === 0 && !isStreaming && !isRestoringHistory && (
            <IcebreakerChips
              items={[
                messages.promptSummarize,
                messages.promptTopProduct,
                messages.promptStoreHours,
                messages.promptInsight,
              ]}
              onSelect={text => {
                void sendMessage(text, partnerId)
              }}
              variant="wrap"
              size="sm"
            />
          )}

          {isRestoringHistory && (
            <StatusRow>
              <FoodLoading size="xs" inline showText={false} />
            </StatusRow>
          )}

          {chatMessages.map(msg => {
            if (msg.role === 'approval') {
              if (msg.status === 'pending') {
                return (
                  <ApprovalCard
                    key={msg.id}
                    preview={msg.preview}
                    locked={msg.locked}
                    onApprove={() => {
                      void confirmApproval(partnerId)
                    }}
                    onCancel={() => cancelApproval(partnerId)}
                  />
                )
              }
              return (
                <ApprovalCardSettled
                  key={msg.id}
                  preview={msg.preview}
                  status={msg.status}
                  settledAt={msg.settledAt ?? 0}
                />
              )
            }
            if (msg.role === 'aioli') {
              const onFollowUp = (text: string) => void sendMessage(text, partnerId)
              return (
                <AioliBlock key={msg.id}>
                  {renderAioliBlocks(msg.blocks, onFollowUp)}
                  {msg.followUpQueries.length > 0 && (
                    <FollowUpPromptChips
                      prompts={msg.followUpQueries}
                      onFollowUpClick={onFollowUp}
                    />
                  )}
                </AioliBlock>
              )
            }

            return (
              <Bubble key={msg.id} isUser={msg.role === 'user'}>
                {msg.role === 'user' ? msg.content : <Markdown>{msg.content}</Markdown>}
              </Bubble>
            )
          })}

          {isToolRunning && (
            <StatusRow>
              <FoodLoading size="xs" inline showText={false} />
              {toolName ?? '…'}
            </StatusRow>
          )}

          {isStreaming && !isToolRunning && (
            <StatusRow>
              <FoodLoading size="xs" inline showText={false} />
            </StatusRow>
          )}

          <div ref={bottomRef} />
        </MessagesPane>

        <InputWrapper>
          <Flex.Row gap={spacing.X8} alignItems="flex-end">
            <ChatTextarea
              ref={chatInputRef}
              value={inputValue}
              rows={1}
              placeholder={
                composerDisabled ? messages.placeholderPendingApproval : messages.placeholder
              }
              disabled={composerDisabled}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => {
                if (
                  e.key === 'Enter' &&
                  !e.shiftKey &&
                  !isStreaming &&
                  !composerDisabled &&
                  inputValue.trim()
                ) {
                  e.preventDefault()
                  void sendMessage(inputValue.trim(), partnerId)
                  setInputValue('')
                }
              }}
            />
            <SendButton
              isStreaming={isStreaming}
              disabled={composerDisabled}
              onClick={() => {
                if (composerDisabled) return
                if (isStreaming) {
                  stopStreaming()
                } else if (inputValue.trim()) {
                  void sendMessage(inputValue.trim(), partnerId)
                  setInputValue('')
                }
              }}
              aria-label={isStreaming ? 'Stop' : 'Send'}
            >
              {isStreaming ? '■' : '↑'}
            </SendButton>
          </Flex.Row>
        </InputWrapper>
      </Sidebar>
    </>,
    document.body
  )
}

export function RetailerAgentOverlay() {
  const { pathname } = useLocation()
  const hasAccess = useAccessControl()
  const { enabled } = useRetailerAgentEnabled()

  if (matchPath(pathname, { path: URL_PARTNER_HALT_TERMS })) return null
  if (!hasAccess({ featureToggles: [FeatureToggle.RetailerAgent] })) return null
  if (!enabled) return null
  return <RetailerAgentOverlayInner />
}
